/**
 * VerifyEmail — Phase 2 (Executive Redesign, Entry Experience).
 *
 * Route: `/verify-email/:token`. Confirms email ownership from the link in
 * the transactional email. Presentation only — API contract, redirect
 * behaviour, and test IDs are preserved verbatim.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Mail } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AuthShell from "./AuthShell";
import { Card, CardBody } from "@/design-system";

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [phase, setPhase] = useState("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify-email/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setMessage(data?.message || "");
        if (data?.verified) {
          setPhase("ok");
          try { await refresh(); } catch { /* tolerated */ }
          setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
        } else {
          setPhase("error");
        }
      } catch (e) {
        if (cancelled) return;
        setMessage("Could not verify this link. It may have expired.");
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate, refresh]);

  const titleMap = {
    loading: "Verifying your email address…",
    ok: "Email verified.",
    error: "Verification failed.",
  };

  return (
    <AuthShell testId="verify-email-page" title={titleMap[phase] || "Verify email"}>
      {phase === "loading" && (
        <Card data-testid="verify-loading">
          <CardBody className="p-6 md:p-8 text-center">
            <Mail
              className="w-7 h-7 mb-3 mx-auto"
              style={{ color: "var(--ds-color-accent)" }}
              aria-hidden
            />
            <p className="text-[15px] text-[var(--ds-color-ink-muted)]">
              Confirming your email address…
            </p>
          </CardBody>
        </Card>
      )}
      {phase === "ok" && (
        <Card data-testid="verify-ok">
          <CardBody className="p-6 md:p-8 text-center">
            <CheckCircle2
              className="w-9 h-9 mb-3 mx-auto"
              style={{ color: "var(--ds-color-success)" }}
              aria-hidden
            />
            <p className="text-[15px] text-[var(--ds-color-ink-muted)]">
              {message || "Thanks for confirming your email."} Redirecting to your dashboard…
            </p>
          </CardBody>
        </Card>
      )}
      {phase === "error" && (
        <Card data-testid="verify-error">
          <CardBody className="p-6 md:p-8">
            <AlertCircle
              className="w-7 h-7 mb-3"
              style={{ color: "var(--ds-color-danger)" }}
              aria-hidden
            />
            <p className="text-[15px] mb-5 text-[var(--ds-color-ink-muted)]">
              {message || "This verification link is no longer valid."}
            </p>
            <Link
              to="/dashboard"
              data-testid="verify-go-dashboard"
              className="text-[13px] font-medium text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)] transition-colors"
            >
              Go to dashboard
            </Link>
          </CardBody>
        </Card>
      )}
    </AuthShell>
  );
}
